import { useEffect, useState } from "react";
import { type Column } from "@/types/array.types"
import "./admin-users.css"
import { formatDateDisplay } from "@/utils/date";
import { createUser, deleteUser, forceLogoutUser, getProfiles, getRoles, inviteUser, updateUser, type CreateUserType, type ProfileType, type ProfileTypeWithRole, type Role } from "./viewModel";
import { UsersTable } from "../../components/users-table";
import Drawer from "@/components/drawer/Drawer";
import { CreateUserForm } from "../../components/create-user-form/CreateUserForm";
import ConfirmModal from "@/components/modal/Modal";
import Select from "@/components/select/Select";
import type { IconName } from "@/components/icon/IconName";



export default function AdminUsers() {

  const [drawer, setDrawer] = useState({isOpen: false, isInvite: false});
  const [roles, setRoles] = useState<Role[]>([]);  
  const [users, setUsers] = useState<any[]>([]);
  const [uuid, setUuid] = useState("");
  const [isDeleteOpen, setDeleteOpen] = useState(false); 

  const columns = [
    {
      key: "full_name",
      label: "Nom",
    },
    {
      key: "roles",
      label: "Role",
      render: (row) => row.roles?.name ?? ""
    },
    {
      key: "is_active",
      label: "Actif",
      render: (row) => row.is_active ? "Oui" : "Non"
    },
    {
      key: "created_at",
      label: "Date de création",
      render: (row) => row ? formatDateDisplay(row.created_at) : "",
    },
    {
      key: "last_login",
      label: "Dernière connexion",
      render: (row) => row ? formatDateDisplay(row.last_login_at) : "",
    },
  ] as Column<ProfileTypeWithRole>[];


  const loadUsers = async () => {
    const data = await getProfiles();
    setUsers(data ?? []);
  };

  const loadRoles = async () => {
    const data = await getRoles();
    setRoles(data ?? []);
  };


  useEffect(() => {
    loadRoles();
    loadUsers();
  }, []);

  const onUpdate = async(id: string, values: Partial<ProfileType>) => {
    await updateUser(id, values);
  };

  const onAdd = (inviteUser?: boolean) => {
    setDrawer({isOpen: true, isInvite: inviteUser});
  };

  const onCancel = () => {
    setDrawer({...drawer, ...{isOpen: false}});
  };

  const onCreate = async (values: CreateUserType) => {
    await createUser(values);
    await loadUsers();
    setDrawer({...drawer, ...{isOpen: false}});
  };

  const onInvite = async (values: CreateUserType) => {
    await inviteUser(values);
    setDrawer({...drawer, ...{isOpen: false}});
  };

  const onDelete = async (id: string) => {
    await deleteUser(id);
    await loadUsers();
  };

  const onSignOut = async (id: string) => {
    await forceLogoutUser(id);
  };


  return (
    <section className="admin-section">
  
      <h2>Utilisateurs</h2>
      <div className="admin-user-create">
          <Select className="admin-user-select" 
            items={[
                {
                  label: "Envoyer une invitation",
                  iconName: "send" as IconName,
                  onClick: () => onAdd(true)
                },
                {
                  label: "Créer un nouvel utilisateur",
                  iconName: "add" as IconName,
                  onClick : () => onAdd(false),
                },
              ]}
            withButton={true}
          />
         
      </div>
      <UsersTable
        users={users}
        columns={columns}
        roles={roles}
        onDelete={(id) => {
          setDeleteOpen(true);
          setUuid(id)}}
        onSignOut={onSignOut}
        onUpdate={onUpdate}
      />
       <Drawer
          isOpen={drawer.isOpen}
          title={drawer.isInvite ? "Inviter un utilisateur" : "Nouvel utilisateur"}
          onClose={() => setDrawer({...drawer, ...{isOpen: false}})}
        >
          <CreateUserForm<CreateUserType> 
            isInvite={drawer.isInvite} 
            roles={roles} 
            onCancel={onCancel} 
            onSubmit={drawer.isInvite ? onInvite : onCreate}
          />
        </Drawer>
        <ConfirmModal
          isOpen={isDeleteOpen}
          title="Supprimer cet utilisateur ?"
          children={
              <>
                  Cette action est <strong>définitive</strong>.
                  <br />
                  L'utilisateur sera supprimé de la base.
              </>
          }
          onCancel={() => setDeleteOpen(false)}
          onConfirm={() => {
            onDelete(uuid);
            setDeleteOpen(false);
          }}
        />
    </section>
  );
}